/**
 * Font Loading Service
 * Dynamically loads font CSS files from assets server (like old lobby)
 */

import { useEffect } from 'react';
import { useAppStore } from 'store/app/appStore';

interface FontLoadingOptions {
  assetsHost: string;
  assetsHash?: string;
}

/**
 * Load font CSS files dynamically from assets server
 * Replicates the old lobby's preloadFontsWithStyles function
 */
export function loadFontsFromAssets({ assetsHost, assetsHash }: FontLoadingOptions): void {
  // For CDN environments, don't proceed if hash is not available
  // This prevents requests without the hash in the path
  if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) {
    return;
  }

  // Font CSS files to load (same as old lobby)
  const fontFiles = [
    'Roboto/Roboto.css',
    'Futura/Futura_PT/Futura-PT.css',
    'Futura/Futura_PT_Cond/Futura-PT-Cond.css',
    'Kranto/Kranto.css',
    'SF-Pro/SF-Pro.css',
    'Numeric/Numeric.css'
  ];

  // Loading fonts from assets server

  fontFiles.forEach((fontFile) => {
    // Check if this font CSS is already loaded
    const existingLink = document.querySelector(`link[href*="${fontFile}"]`);
    if (existingLink) {
      // Font already loaded, skipping
      return;
    }

    // Construct URL with hash (same pattern as old lobby)
    // For local development (localhost), don't use hash paths
    const isLocalhost = assetsHost.includes('localhost');
    const baseUrl = isLocalhost
      ? `${assetsHost}/assets/Fonts/${fontFile}`
      : `${assetsHost}${assetsHash ? '/' + assetsHash : ''}/assets/Fonts/${fontFile}`;
    const urlWithHash = assetsHash && !isLocalhost ? `${baseUrl}?hash=${assetsHash}` : baseUrl;

    // Create preload link first for better performance
    const preloadLink = document.createElement('link');
    preloadLink.setAttribute('rel', 'preload');
    preloadLink.setAttribute('as', 'style');
    preloadLink.setAttribute('href', urlWithHash);
    preloadLink.setAttribute('data-font-preload', fontFile);
    document.head.appendChild(preloadLink);

    // Create and append actual stylesheet link
    const linkElement = document.createElement('link');
    linkElement.setAttribute('rel', 'stylesheet');
    linkElement.setAttribute('type', 'text/css');
    linkElement.setAttribute('href', urlWithHash);
    linkElement.setAttribute('data-font-source', fontFile);

    // Add error handling
    linkElement.addEventListener('load', () => {
      // Font loaded successfully - remove preload link as it's no longer needed
      const preload = document.querySelector(`link[data-font-preload="${fontFile}"]`);
      if (preload) {
        preload.remove();
      }
    });

    linkElement.addEventListener('error', () => {
      // Font loading failed - silently handle the error without logging to console
      // This prevents console pollution in environments where fonts may not be available
    });

    document.head.appendChild(linkElement);
    // Loading font CSS
  });
}

/**
 * Remove fonts from document head (cleanup)
 */
function cleanupFonts(): void {
  // Remove stylesheet links
  const fontLinks = document.querySelectorAll('link[data-font-source]');
  fontLinks.forEach(link => {
    document.head.removeChild(link);
  });

  // Remove any remaining preload links
  const preloadLinks = document.querySelectorAll('link[data-font-preload]');
  preloadLinks.forEach(link => {
    document.head.removeChild(link);
  });

  // Font links cleaned up
}

/**
 * Hook to automatically load fonts when assets are available
 */
export function useFontLoader(): void {
  const settings = useAppStore((state) => state.settings);

  // Load fonts when assetsHost AND assetsHash are available
  useEffect(() => {
    const assetsHost = settings.assetsHost;
    const assetsHash = settings.assetsHash;

    // Don't attempt to load fonts if assetsHost is not properly configured
    // This prevents fallback to localhost in non-local environments
    if (!assetsHost || assetsHost === '') {
      // Waiting for assetsHost configuration...
      return;
    }

    // Skip if assetsHost is example.com (mock mode)
    if (assetsHost.includes('example.com')) {
      // Mock mode detected, skipping font loading
      return;
    }

    // Only use localhost:3001 in actual development environment with explicit check
    if (process.env['NODE_ENV'] === 'development' && assetsHost === 'http://localhost:3001') {
      // Development mode with local assets server
      loadFontsFromAssets({
        assetsHost: 'http://localhost:3001',
        assetsHash: ''
      });
      return;
    }

    // For CDN environments, wait for assetsHash to be available
    // This prevents requests without the hash in the path
    if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) {
      // Waiting for assetsHash to be loaded from CDN...
      return;
    }

    // Load fonts with proper configuration
    loadFontsFromAssets({
      assetsHost,
      assetsHash
    });

    return () => {
      // Cleanup on unmount
      cleanupFonts();
    };
  }, [settings.assetsHost, settings.assetsHash]);
}
